import type { AllowedActionable } from 'pkg/api/models/allowed_actionable';
import type { Dateable } from 'pkg/api/models/dateable';
import type { Question, ScoreGuide } from 'pkg/api/models/evaluation';
import type { PublishMode, RatersMode } from 'pkg/api/models/evaluation_cycle';
import type { Record } from 'pkg/api/models/record';
import type { User } from 'pkg/api/models/user';

// Whether a run is before its deadline. A closed run may or may not be
// published; `publishedAt` says which, and one kept with coaches never is.
export enum RunStatus {
	Open = 'open',
	Closed = 'closed',
}

export enum MemberRole {
	Subject = 'subject',
	Rater = 'rater',
}

export enum AssessmentStatus {
	Draft = 'draft',
	Submitted = 'submitted',
}

// Someone a run reached for one evaluation: a person being rated, with the
// group they were reached through (none when named), or a rater.
export interface RunMember extends Record {
	runEvaluationId: number;
	userId: number;
	role: MemberRole;
	viaGroupId: number | null;
	// The chosen groups the member was in when the run opened: for a
	// person rated, every one they play in, viaGroupId first; for a rater,
	// the ones whose players they rate. Absent for someone named by hand,
	// for a rater who rates everyone, and on runs opened before it was kept
	// (SPO-1336). `rates` in the cycle's completion pairs them.
	groupIds?: number[];
	// A self-assessment is expected of this subject: they have an account,
	// or a linked parent may answer for them. Only on evaluations players
	// rate themselves in; absent reads as false (SPO-1302).
	selfAssessmentDue?: boolean;
	user?: User;
}

// One criterion as it was when the run opened.
export interface RunCriterion {
	libraryTagId: number;
	name: string;
	guide: ScoreGuide;
	allowCantRate: boolean;
}

// An evaluation as it was when the run opened. Later edits never reach it.
export interface RunDefinition {
	title: string;
	description: string | null;
	criteria: RunCriterion[];
	questions: Question[];
}

// Where one rater stands with one person: statuses only, never answers or
// scores. A self-assessment has the person as its own rater, and names the
// parent who answered for them, if one did.
export interface Assessment {
	subjectUserId: number;
	raterUserId: number;
	answeredByUserId: number | null;
	status: AssessmentStatus;
	submittedAt: number | null;
	updatedAt: number;
}

// One evaluation inside a run, frozen when it opened, with who it reached.
export interface RunEvaluation extends Record {
	runId: number;
	evaluationId: number;
	definition: RunDefinition;
	ratersMode: RatersMode;
	parentsCannotAnswer: boolean;
	// Left out when the run reached nobody for this evaluation.
	members?: RunMember[];
	// Where each rater stands, statuses only. Left out while none is saved,
	// so absent reads as none.
	assessments?: Assessment[];
}

// One round of a sent evaluation, "Cycle N" in the UI.
export interface Run extends Record, Dateable, AllowedActionable {
	publicId: string;
	cycleId: number;
	number: number;
	opensAt: number;
	closesAt: number;
	status: RunStatus;
	publishedAt: number | null;
	// The cycle's publish mode when the run opened; a later edit of the
	// cycle never changes it. Absent from an API without it, which reads as
	// a coach publishing.
	publishMode?: PublishMode;
	// Loaded when one run is shown, not in the list.
	evaluations?: RunEvaluation[];
	// When its outstanding people were last reminded, unix seconds, on the
	// run shown alone. Absent when nobody has been, or the API no longer
	// knows: it keeps the time for a while, not for good.
	remindedAt?: number;
	// The name of the sent evaluation it belongs to, on the run shown alone,
	// for raters who cannot read the cycle itself.
	cycleName?: string;
	// Whether its sent evaluation repeats, on the run shown alone; read it
	// through isNumbered.
	repeats?: boolean;
	// How many people a remind would reach now, on the run shown alone, for
	// whoever may remind: raters who lost access are left out.
	remindable?: number;
	// On the run shown alone to a rater who rates only some groups' players
	// (a team's coach on a club send to several teams): those groups. The
	// run then holds only their players, the raters who rate them and their
	// progress. Absent when the whole run is shown.
	narrowedTo?: number[];
	// On the run shown alone: the names of the groups its people came
	// through, by id, for the grid's team headings. Absent when nobody came
	// through a group.
	groupNames?: { [groupId: number]: string };
}
